import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Palette,
  Refresh,
  Download,
  ShoppingCart,
  ColorLens,
  Brush,
  Visibility,
  VisibilityOff,
  TextFields,
  Image,
  SportsSoccer,
  SportsBasketball,
  SportsCricket,
  SportsHockey
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { formatMoney } from '../utils/currency';

// Sample logo options
const LOGO_OPTIONS = {
  football: [
    { id: 'football', name: 'Football Ball', icon: '⚽', svg: '⚽' },
    { id: 'shield', name: 'Team Shield', icon: '🛡️', svg: '🛡️' },
    { id: 'star', name: 'Star', icon: '⭐', svg: '⭐' },
    { id: 'crown', name: 'Crown', icon: '👑', svg: '👑' }
  ],
  basketball: [
    { id: 'basketball', name: 'Basketball', icon: '🏀', svg: '🏀' },
    { id: 'hoop', name: 'Basketball Hoop', icon: '🏀', svg: '🏀' },
    { id: 'star', name: 'Star', icon: '⭐', svg: '⭐' },
    { id: 'lightning', name: 'Lightning', icon: '⚡', svg: '⚡' }
  ],
  cricket: [
    { id: 'cricket', name: 'Cricket Ball', icon: '🏏', svg: '🏏' },
    { id: 'bat', name: 'Cricket Bat', icon: '🏏', svg: '🏏' },
    { id: 'stumps', name: 'Stumps', icon: '🏏', svg: '🏏' },
    { id: 'star', name: 'Star', icon: '⭐', svg: '⭐' }
  ],
  hockey: [
    { id: 'hockey', name: 'Hockey Stick', icon: '🏒', svg: '🏒' },
    { id: 'puck', name: 'Hockey Puck', icon: '🏒', svg: '🏒' },
    { id: 'star', name: 'Star', icon: '⭐', svg: '⭐' },
    { id: 'flame', name: 'Flame', icon: '🔥', svg: '🔥' }
  ]
};

// Sample number styles
const NUMBER_STYLES = [
  { id: 'classic', name: 'Classic', font: 'Arial Black' },
  { id: 'modern', name: 'Modern', font: 'Impact' },
  { id: 'elegant', name: 'Elegant', font: 'Georgia' },
  { id: 'bold', name: 'Bold', font: 'Verdana' }
];

function LogoCustomization() {
  const location = useLocation();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [design, setDesign] = useState(null);
  const [category, setCategory] = useState('');
  const [colors, setColors] = useState({});
  const [size, setSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  
  // Logo customization
  const [selectedLogo, setSelectedLogo] = useState('football');
  const [logoColor, setLogoColor] = useState('#ffffff');
  const [logoSize, setLogoSize] = useState('medium');
  
  // Number customization
  const [playerNumber, setPlayerNumber] = useState('10');
  const [numberColor, setNumberColor] = useState('#000000');
  const [numberStyle, setNumberStyle] = useState('classic');
  const [numberSize, setNumberSize] = useState('large');
  
  // Team details
  const [teamName, setTeamName] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  
  // Preview
  const [showPreview, setShowPreview] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (location.state?.design && location.state?.category) {
      setDesign(location.state.design);
      setCategory(location.state.category);
      setColors(location.state.colors || {});
      setSize(location.state.size || 'M');
      setQuantity(location.state.quantity || 1);
      
      // Set default logo based on category
      setSelectedLogo(LOGO_OPTIONS[location.state.category]?.[0]?.id || 'football');
    }
  }, [location.state]);

  const handleContinue = () => {
    if (!teamName.trim()) {
      setSnackbarMessage('Please enter a team name');
      setSnackbarOpen(true);
      return;
    }
    
    if (!playerNumber.trim()) {
      setSnackbarMessage('Please enter a player number');
      setSnackbarOpen(true);
      return;
    }
    
    navigate('/order-form', {
      state: {
        design,
        category,
        colors,
        size,
        quantity,
        logo: {
          type: selectedLogo,
          color: logoColor,
          size: logoSize
        },
        number: {
          value: playerNumber,
          color: numberColor,
          style: numberStyle,
          size: numberSize
        },
        team: {
          name: teamName,
          player: playerName,
          sponsor: sponsorName
        }
      }
    });
  };

  const getLogoIcon = () => {
    const logo = LOGO_OPTIONS[category]?.find(l => l.id === selectedLogo);
    return logo?.icon || '⚽';
  };

  const getLogoSize = () => {
    const sizes = { small: 24, medium: 32, large: 40 };
    return sizes[logoSize] || 32;
  };

  const getNumberSize = () => {
    const sizes = { small: 32, medium: 48, large: 64 };
    return sizes[numberSize] || 48;
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
      <MuiLink component={Link} to="/" color="inherit">
        Home
      </MuiLink>
      <MuiLink component={Link} to={`/${category}`} color="inherit">
        {category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Category'}
      </MuiLink>
      <MuiLink component={Link} to="/customize" color="inherit">
        Customize Colors
      </MuiLink>
      <Typography color="text.primary">
        Logo & Number
      </Typography>
    </Breadcrumbs>
  );

  const renderPreview = () => (
    <Grid item xs={12} lg={8}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              Final Preview
            </Typography>
            <Button
              startIcon={showPreview ? <VisibilityOff /> : <Visibility />}
              onClick={() => setShowPreview(!showPreview)}
              variant="outlined"
              size="small"
            >
              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </Box>
          
          {showPreview && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '500px',
                position: 'relative',
                background: 'transparent',
                overflow: 'hidden',
                borderRadius: 2,
                p: 2,
                '& > div': {
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))'
                }
              }}
            >
              <div
                ref={previewRef}
                style={{
                  width: '300px',
                  height: '400px',
                  position: 'relative',
                  backgroundColor: colors.primary || '#1976d2',
                  borderRadius: '20px',
                  border: `3px solid ${colors.secondary || '#ffffff'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px'
                }}
              >
                {/* Logo */}
                <Box
                  sx={{
                    fontSize: getLogoSize(),
                    color: logoColor,
                    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))'
                  }}
                >
                  {getLogoIcon()}
                </Box>
                
                {/* Team Name */}
                {teamName && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.secondary || '#ffffff',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                    }}
                  >
                    {teamName.toUpperCase()}
                  </Typography>
                )}
                
                {/* Player Number */}
                <Box
                  sx={{
                    backgroundColor: colors.secondary || '#ffffff',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: numberColor,
                      fontFamily: NUMBER_STYLES.find(s => s.id === numberStyle)?.font || 'Arial Black',
                      fontWeight: 'bold',
                      fontSize: getNumberSize()
                    }}
                  >
                    {playerNumber}
                  </Typography>
                </Box>
                
                {/* Player Name */}
                {playerName && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.accent || '#f57c00',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {playerName.toUpperCase()}
                  </Typography>
                )}
                
                {/* Sponsor */}
                {sponsorName && (
                  <Box
                    sx={{
                      backgroundColor: colors.accent || '#f57c00',
                      borderRadius: '5px',
                      padding: '5px 15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.secondary || '#ffffff',
                        fontWeight: 'bold'
                      }}
                    >
                      {sponsorName.toUpperCase()}
                    </Typography>
                  </Box>
                )}
              </div>
            </Box>
          )}
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={() => {
                setSnackbarMessage('Download feature coming soon!');
                setSnackbarOpen(true);
              }}
            >
              Download Final Design
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderCustomizationControls = () => (
    <Grid item xs={12} lg={4}>
      <Stack spacing={3}>
        {/* Logo Customization */}
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <Image sx={{ mr: 1, verticalAlign: 'middle' }} />
              Logo Selection
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Choose Logo</FormLabel>
              <RadioGroup
                value={selectedLogo}
                onChange={(e) => setSelectedLogo(e.target.value)}
              >
                {LOGO_OPTIONS[category]?.map((logo) => (
                  <FormControlLabel
                    key={logo.id}
                    value={logo.id}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '24px' }}>{logo.icon}</span>
                        <span>{logo.name}</span>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Logo Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={logoColor}
                  onChange={(e) => setLogoColor(e.target.value)}
                  style={{
                    width: '50px',
                    height: '40px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {logoColor}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Logo Size
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={logoSize}
                  onChange={(e) => setLogoSize(e.target.value)}
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Number Customization */}
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <TextFields sx={{ mr: 1, verticalAlign: 'middle' }} />
              Number Customization
            </Typography>
            
            <TextField
              fullWidth
              label="Player Number"
              value={playerNumber}
              onChange={(e) => setPlayerNumber(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 2 }}
            />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Number Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={numberColor}
                  onChange={(e) => setNumberColor(e.target.value)}
                  style={{
                    width: '50px',
                    height: '40px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {numberColor}
                </Typography>
              </Box>
            </Box>
            
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Number Style</InputLabel>
              <Select
                value={numberStyle}
                label="Number Style"
                onChange={(e) => setNumberStyle(e.target.value)}
              >
                {NUMBER_STYLES.map((style) => (
                  <MenuItem key={style.id} value={style.id}>
                    {style.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>Number Size</InputLabel>
              <Select
                value={numberSize}
                label="Number Size"
                onChange={(e) => setNumberSize(e.target.value)}
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Team Details */}
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <SportsSoccer sx={{ mr: 1, verticalAlign: 'middle' }} />
              Team Details
            </Typography>
            
            <TextField
              fullWidth
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              sx={{ mb: 2 }}
              required
              helperText="Required"
            />
            
            <TextField
              fullWidth
              label="Player Name (Optional)"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Sponsor Name (Optional)"
              value={sponsorName}
              onChange={(e) => setSponsorName(e.target.value)}
              sx={{ mb: 3 }}
            />
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={handleContinue}
            >
              Continue to Order
            </Button>
          </CardContent>
        </Card>

        {/* Order Summary */}
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <ShoppingCart sx={{ mr: 1, verticalAlign: 'middle' }} />
              Order Summary
            </Typography>
            
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Design:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {design?.name || 'Custom Design'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Size:</Typography>
                <Typography variant="body2" fontWeight="bold">{size}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Quantity:</Typography>
                <Typography variant="body2" fontWeight="bold">{quantity}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight="bold">Total:</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {formatMoney((design?.price || 29.99) * quantity)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Grid>
  );

  if (!design) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          No Design Selected
        </Typography>
        <Button component={Link} to="/" variant="contained">
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {renderBreadcrumbs()}
      
      <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4 }}>
        Customize Logo & Numbers
      </Typography>
      
      <Grid container spacing={4}>
        {renderPreview()}
        {renderCustomizationControls()}
      </Grid>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default LogoCustomization;
